import { useEffect, useMemo, useState, type ReactNode } from "react";
import { BookOpen, CalendarDays, Check, EyeOff, Lock, Plus } from "lucide-react";
import {
  ASSISTANTS,
  journalPreview,
  monthKeyOf,
  type AssistantConnection,
  type AssistantId,
  type AssistantPermissions,
} from "@workspace/assistants";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMomentum } from "@/hooks/use-momentum";
import { useDisconnectAssistant, useUpdateAssistantPermissions, type AssistantCalendar } from "@/hooks/use-assistants";
import { showPillToast } from "@/components/calendar/pill-toast";
import { AccessSwitch, BODY, ConnectMarks, INK, MUTED, PillButton, Segmented, Tag } from "@/components/assistant/assistant-ui";
import { cn } from "@/lib/utils";

/**
 * AI 17: "What Claude can see and do". The permission ladder for one assistant, with
 * journal access as its own switch (summaries or full, 24 hours or until off) and a
 * preview of exactly what the assistant will see. Disconnect lives here too.
 */
export function AssistantAccessDialog({
  connection,
  calendar,
  onOpenChange,
}: {
  connection: AssistantConnection | null;
  /** Google Calendar as the calendar layer sees it (`useAssistantCalendar`). */
  calendar: AssistantCalendar;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={connection !== null} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-[rgba(47,41,37,0.28)]"
        className="max-h-[94dvh] max-w-[580px] gap-[18px] overflow-y-auto rounded-[28px] border-0 bg-white p-6 shadow-[0_24px_60px_rgba(28,25,23,0.25)] sm:rounded-[28px] sm:p-8 [&>button:last-child]:right-6 [&>button:last-child]:top-6 sm:[&>button:last-child]:right-8 sm:[&>button:last-child]:top-8"
      >
        {connection && (
          <AccessForm
            key={connection.assistant}
            connection={connection}
            calendar={calendar}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const CALENDAR_DETAIL: Record<AssistantCalendar, string> = {
  connected: "Uses your connected Google Calendar.",
  needs_reconnect: "Reconnect Google Calendar in Settings first.",
  not_connected: "Connect Google Calendar in Settings first.",
};

function AccessForm({ connection, calendar, onDone }: { connection: AssistantConnection; calendar: AssistantCalendar; onDone: () => void }) {
  const assistant = connection.assistant;
  const name = ASSISTANTS[assistant].name;
  const [draft, setDraft] = useState<AssistantPermissions>(connection.permissions);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateAssistantPermissions(assistant);
  const disconnect = useDisconnectAssistant(assistant);
  const calendarConnected = calendar === "connected";
  const journalWasOn = connection.permissions.journal.enabled;
  const journalCount = useThisMonthUnfoldings();
  const [now] = useState(() => new Date());

  // Keep the form in step if access ends while the dialog is open.
  useEffect(() => setDraft(connection.permissions), [connection.permissions]);

  const set = <K extends keyof AssistantPermissions>(key: K, value: AssistantPermissions[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const setJournal = (patch: Partial<AssistantPermissions["journal"]>) => setDraft((d) => ({ ...d, journal: { ...d.journal, ...patch } }));

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        actions: draft.actions,
        calendar: draft.calendar,
        suggestSteps: draft.suggestSteps,
        journal: { enabled: draft.journal.enabled, mode: draft.journal.mode, duration: draft.journal.duration },
      });
      onDone();
    } catch (err) {
      setError((err as Error).message || "Couldn't save. Try again in a moment.");
    }
  }

  async function doDisconnect() {
    try {
      await disconnect.mutateAsync();
      setConfirmDisconnect(false);
      onDone();
      // Same pill as Settings › Calendar's "Google Calendar disconnected".
      showPillToast({ message: `${name} disconnected · it can't see or change anything now` });
    } catch {
      setError(`Couldn't disconnect ${name}. Try again in a moment.`);
    }
  }

  return (
    <>
      <ConnectMarks assistant={assistant} />
      <DialogTitle className={`text-[26px] font-semibold leading-8 tracking-[-0.04em] ${INK}`}>What {name} can see and do</DialogTitle>
      <DialogDescription className={`-mt-1 text-[15px] leading-[22px] ${MUTED}`}>
        Changes apply right away. {name} still asks before every action.
      </DialogDescription>

      <div className="overflow-hidden rounded-2xl border border-[rgba(47,41,37,0.1)]">
        <Row
          icon={<BookOpen />}
          title="See your open Next Steps"
          tag={<Tag>Required</Tag>}
          detail="Step text, month, source title and scheduled time."
          control={<AccessSwitch checked locked label="See your open Next Steps (required)" />}
        />
        <Row
          icon={<Check />}
          title="Tick steps done and add notes"
          detail={`${name} asks before each action.`}
          control={<AccessSwitch checked={draft.actions} onCheckedChange={(v) => set("actions", v)} label="Tick steps done and add notes" />}
        />
        <Row
          icon={<CalendarDays />}
          title="Add steps to your calendar"
          detail={CALENDAR_DETAIL[calendar]}
          control={
            <AccessSwitch
              checked={draft.calendar && calendarConnected}
              disabled={!calendarConnected}
              onCheckedChange={(v) => set("calendar", v)}
              label="Add steps to your calendar"
            />
          }
        />
        <Row
          icon={<Lock />}
          title="Read your journal entries"
          tag={draft.journal.enabled && !journalWasOn ? <Tag tone="purple">Just turned on</Tag> : null}
          detail="The reflections behind your steps."
          highlighted={draft.journal.enabled}
          control={<AccessSwitch checked={draft.journal.enabled} onCheckedChange={(v) => setJournal({ enabled: v })} label="Read your journal entries" />}
        >
          {draft.journal.enabled && (
            <div className="flex flex-col gap-[10px] sm:px-7">
              <Option label={`What ${name} sees`}>
                <Segmented
                  label={`What ${name} sees`}
                  value={draft.journal.mode}
                  onChange={(mode) => setJournal({ mode })}
                  options={[
                    { value: "summaries", label: "Summaries" },
                    { value: "full", label: "Full entries" },
                  ]}
                />
              </Option>
              <Option label="For how long">
                <Segmented
                  label="For how long"
                  value={draft.journal.duration}
                  onChange={(duration) => setJournal({ duration })}
                  options={[
                    { value: "24h", label: "24 hours" },
                    { value: "until_off", label: "Until I turn it off" },
                  ]}
                />
              </Option>
              <div className="flex gap-2 rounded-xl bg-white p-3">
                <EyeOff className="mt-0.5 size-3.5 shrink-0 text-[#6e53ac]" />
                <p className={`text-xs leading-[17px] ${BODY}`}>
                  {journalAccessText(draft, connection, assistant, journalCount, now)}
                </p>
              </div>
            </div>
          )}
        </Row>
        <Row
          icon={<Plus />}
          title="Suggest new steps"
          tag={<Tag tone="amber">Off by default</Tag>}
          detail={`${name} can add a step after you approve it. Marked “Suggested with ${name}”.`}
          control={<AccessSwitch checked={draft.suggestSteps} onCheckedChange={(v) => set("suggestSteps", v)} label="Suggest new steps" />}
          last
        />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="h-px bg-[rgba(47,41,37,0.08)]" />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setConfirmDisconnect(true)}
          className="mr-auto rounded-md text-[13px] font-semibold text-[#a33a2b] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40"
        >
          Disconnect {name}
        </button>
        <PillButton variant="outline" onClick={onDone}>
          Cancel
        </PillButton>
        <PillButton onClick={() => void save()} disabled={update.isPending}>
          {update.isPending ? "Saving…" : "Save"}
        </PillButton>
      </div>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent className="rounded-[24px] bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {name} loses access to UnfoldWIN at once. Steps it marked and notes it saved stay as they are. You can connect again
              anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[22px]">Keep connected</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void doDisconnect();
              }}
              className="rounded-[22px] bg-[#a33a2b] text-white hover:bg-[#a33a2b]/90"
            >
              {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** The preview. When access is already running unchanged, it ends when it was going to, not 24 hours from now. */
function journalAccessText(
  draft: AssistantPermissions,
  connection: AssistantConnection,
  assistant: AssistantId,
  count: number,
  now: Date,
): string {
  const saved = connection.permissions.journal;
  const running = saved.enabled && saved.duration === draft.journal.duration;
  return journalPreview({
    assistant,
    count,
    month: monthKeyOf(now),
    mode: draft.journal.mode,
    duration: draft.journal.duration,
    now,
    endsAt: running ? saved.expiresAt : null,
  });
}

/** How many of this month's Unfoldings journal access would cover. */
function useThisMonthUnfoldings(): number {
  const { data } = useMomentum();
  return useMemo(() => {
    const month = monthKeyOf(new Date());
    return new Set((data ?? []).filter((i) => monthKeyOf(new Date(i.journalCreatedAt)) === month).map((i) => i.journalId)).size;
  }, [data]);
}

function Row({
  icon,
  title,
  tag,
  detail,
  control,
  highlighted = false,
  last = false,
  children,
}: {
  icon: ReactNode;
  title: string;
  tag?: ReactNode;
  detail: string;
  control: ReactNode;
  highlighted?: boolean;
  last?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-3 px-4 py-3.5", !last && "border-b border-[rgba(47,41,37,0.08)]", highlighted && "bg-[rgba(233,226,246,0.4)]")}>
      <div className="flex items-center gap-3">
        <span className={cn("shrink-0 [&>svg]:size-4", highlighted ? "text-[#6e53ac]" : MUTED)}>{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className={`text-sm font-semibold ${INK}`}>{title}</p>
            {tag}
          </div>
          <p className={`mt-0.5 text-xs leading-[17px] ${MUTED}`}>{detail}</p>
        </div>
        {control}
      </div>
      {children}
    </div>
  );
}

function Option({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-[10px]">
      <p className={`min-w-[7rem] flex-1 text-xs font-medium ${BODY}`}>{label}</p>
      {children}
    </div>
  );
}
